import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatInr } from "@/lib/currency";
import { StatusBadge } from "@/components/StatusBadge";
import { FileText, CheckCircle, WarningOctagon, XCircle, Users, ArrowRight } from "@phosphor-icons/react";

function Kpi({ label, value, icon: Icon, testId, tone = "default" }) {
  const toneMap = {
    default: "text-primary",
    success: "text-emerald-700",
    warning: "text-amber-700",
    danger: "text-rose-700",
    neutral: "text-slate-700",
  };
  return (
    <div className="kpi-card" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <Icon size={20} weight="duotone" className={toneMap[tone]} />
      </div>
      <div className={`mt-3 font-display text-2xl font-semibold ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch((e) => setErr(String(e)));
  }, []);

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="font-display text-3xl tracking-tight font-bold text-slate-900" data-testid="dashboard-title">
          Dashboard
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Overview of imports, drafts, approvals and reconciliation status.
        </p>
      </div>

      {err && <div className="text-destructive text-sm">{err}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi label="Imported Parties" value={stats?.parties_count ?? "—"} icon={Users} testId="kpi-parties" />
        <Kpi label="Imported Value" value={stats ? formatInr(stats.imported_total) : "—"} icon={FileText} testId="kpi-imported-total" />
        <Kpi label="Draft Invoices" value={stats?.drafts ?? "—"} icon={FileText} testId="kpi-drafts" tone="warning" />
        <Kpi label="Approved" value={stats?.approved ?? "—"} icon={CheckCircle} testId="kpi-approved" tone="success" />
        <Kpi label="Voided / Rejected" value={((stats?.voided || 0) + (stats?.rejected || 0)) || "—"} icon={XCircle} testId="kpi-voided" tone="danger" />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="card-panel p-5" data-testid="reconciliation-summary">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold">Reconciliation</div>
            <Link to="/reconciliation" className="text-xs text-primary hover:underline flex items-center gap-1">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Balanced</span><span className="num font-medium text-emerald-700">{stats?.reconciliation?.balanced ?? 0}</span></div>
            <div className="flex justify-between"><span>Awaiting Approval</span><span className="num font-medium text-amber-700">{stats?.reconciliation?.awaiting_approval ?? 0}</span></div>
            <div className="flex justify-between"><span>Approved Parties</span><span className="num font-medium text-emerald-800">{stats?.reconciliation?.approved_parties ?? 0}</span></div>
            <div className="flex justify-between"><span>Unbalanced</span><span className="num font-medium text-rose-700">{stats?.reconciliation?.unbalanced ?? 0}</span></div>
          </div>
        </div>

        <div className="card-panel p-5 md:col-span-2">
          <div className="font-display font-semibold mb-3">Recent uploads</div>
          <div className="space-y-2 text-sm">
            {(stats?.recent_uploads || []).map((u) => (
              <div key={u.id} className="flex items-center justify-between border-b border-border/60 pb-2 last:border-b-0">
                <div>
                  <Link to={`/batches/${u.id}`} className="hover:underline" data-testid={`recent-upload-${u.id}`}>
                    {u.filename}
                  </Link>
                  <div className="text-xs text-muted-foreground">
                    {u.total_rows} rows · {u.valid_rows} valid · {u.invalid_rows} invalid
                  </div>
                </div>
                <StatusBadge status={u.status} />
              </div>
            ))}
            {(!stats?.recent_uploads || stats.recent_uploads.length === 0) && (
              <div className="text-muted-foreground text-sm">No uploads yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
